import { interpolateNumber } from 'd3-interpolate'

import { SankeyData } from './models/sankey-data.model'
import { CONST } from './models/sankey-layout.model'
import { formatService } from '../../common/format.service'

class SankeyService {
  getSankeyData (clusters, demographicCategory, { stageLabels, stateLabels, width, height, stateColors, demographicColors, valueLabel, axisLabel }) {
    const sankeyData = new SankeyData(clusters, demographicCategory)
    sankeyData.layout.setLayout({ width, height, stageLabels, stateLabels, stateColors, demographicColors, valueLabel, axisLabel })
    this.calculateSegmentLayout(sankeyData)
    this.calculateFlowLayout(sankeyData)
    return sankeyData
  }

  calculateSegmentLayout (sankeyData) {
    const segmentsData = sankeyData.segments.getAllSegments()

    let leftSideOfSegment = CONST.LEFT_RIGHT_PADDING
    for (const stage in segmentsData) {
      const stateSegmentsInStage = segmentsData[stage]
      let stateSegmentHeightCursor = CONST.TOP_BOTTOM_PADDING

      for (const state in stateSegmentsInStage) {
        const demographicValueSegmentsInState = stateSegmentsInStage[state]
        const stateSegmentHeight = this.roundToThousandthPlace(sankeyData.layout.totalStateSegmentHeight * sankeyData.getStateTotalSize(stage, state) / sankeyData.getStageTotalSize(stage))
        let segmentHeightCursor = stateSegmentHeightCursor

        for (const demographicValue in demographicValueSegmentsInState) {
          const segment = demographicValueSegmentsInState[demographicValue]
          let segHeight = this.roundToThousandthPlace(stateSegmentHeight * segment.size / sankeyData.getStateTotalSize(stage, state))

          if (segHeight < CONST.MIN_SEGMENT_HEIGHT) {
            segHeight = CONST.MIN_SEGMENT_HEIGHT
          }

          sankeyData.segments.setSegment(stage, state, demographicValue, {
            top: segmentHeightCursor,
            left: leftSideOfSegment,
            width: CONST.SEGMENT_WIDTH,
            height: segHeight
          })

          segmentHeightCursor += segHeight
        }

        stateSegmentHeightCursor += stateSegmentHeight + CONST.STATE_SEGMENT_GAP
      }

      leftSideOfSegment += CONST.SEGMENT_WIDTH + sankeyData.layout.gapBetweenStages
    }
  }

  calculateFlowLayout (sankeyData, isSelectedFlow = false) {
    const iterator = isSelectedFlow ? sankeyData.iterateThroughSelectedFlows.bind(sankeyData) : sankeyData.iterateThroughFlows.bind(sankeyData)
    const flows = isSelectedFlow ? sankeyData.selectedFlows : sankeyData.flows

    const outflowSegmentCursors = new Map()
    const inflowSegmentCursors = new Map()
    const getKey = (stage, state, demographicValue) => (`${stage}-${state}-${demographicValue}`)

    iterator((flow, stage, fromState, toState, demographicValue) => {
      const toStage = parseInt(stage) + 1
      const fromStateSegment = sankeyData.segments.getSegment(stage, fromState, demographicValue)
      const toStateSegment = sankeyData.segments.getSegment(toStage, toState, demographicValue)

      const outflowCursorKey = getKey(stage, fromState, demographicValue)
      const inflowCursorKey = getKey(stage, toState, demographicValue)
      const outflowCursor = outflowSegmentCursors.get(outflowCursorKey) || fromStateSegment.top
      const inflowCursor = inflowSegmentCursors.get(inflowCursorKey) || toStateSegment.top

      const height1 = fromStateSegment.height * flow.size / fromStateSegment.size
      const height2 = toStateSegment.height * flow.size / toStateSegment.size
      const isHeight1LessThanMinHeight = height1 < CONST.MIN_FLOW_HEIGHT
      const isHeight2LessThanMinHeight = height2 < CONST.MIN_FLOW_HEIGHT

      flows.setFlow(stage, fromState, toState, demographicValue, {
        x1: fromStateSegment.left + CONST.SEGMENT_WIDTH,
        y1: outflowCursor,
        height1: isHeight1LessThanMinHeight ? CONST.MIN_FLOW_HEIGHT : height1,
        x2: toStateSegment.left,
        y2: inflowCursor,
        height2: isHeight2LessThanMinHeight ? CONST.MIN_FLOW_HEIGHT : height2
      })

      outflowSegmentCursors.set(outflowCursorKey, isHeight1LessThanMinHeight ? flow.y1 : (flow.y1 + flow.height1))
      inflowSegmentCursors.set(inflowCursorKey, isHeight2LessThanMinHeight ? flow.y2 : (flow.y2 + flow.height2))
    })
  }

  selectSegment (sankeyData, selectedStage, selectedState, selectedDemographicValue) {
    const selectedSegment = sankeyData.segments.getSegment(selectedStage, selectedState, selectedDemographicValue)
    selectedSegment.clusters.forEach((cluster) => {
      const [, demographicValue, , size, ...states] = cluster

      states.forEach((state, stage) => {
        sankeyData.selectedSegments.add(stage, state, demographicValue, size)
      })

      for (let stage = 0; stage < states.length - 1; stage++) {
        sankeyData.selectedFlows.add(stage, states[stage], states[stage + 1], demographicValue, size)
      }
    })

    sankeyData.iterateThroughSelectedSegments((segment, stage, state, demographicValue) => {
      const fullSegment = sankeyData.segments.getSegment(stage, state, demographicValue)
      sankeyData.selectedSegments.setSegment(stage, state, demographicValue, {
        top: fullSegment.top,
        left: fullSegment.left,
        width: fullSegment.width,
        height: this.roundToThousandthPlace(fullSegment.height * segment.size / fullSegment.size),
        isPrimarySegment: (stage === selectedStage) && (state === selectedState) && (demographicValue === selectedDemographicValue)
      })
    })

    this.calculateFlowLayout(sankeyData, true)

    return selectedSegment
  }

  deselectSegment (sankeyData) {
    sankeyData.clearSelectedSegmentsAndFlows()
  }

  generatePath ({ x1, y1, height1, x2, y2, height2 }) {
    const xi = interpolateNumber(x1, x2)
    const xc0 = xi(CONST.PATH_CURVATURE)
    const xc1 = xi(1 - CONST.PATH_CURVATURE)

    return 'M' + this.roundToThousandthPlace(x1) + ',' + this.roundToThousandthPlace(y1) +
          'C' + this.roundToThousandthPlace(xc0) + ',' + this.roundToThousandthPlace(y1) +
          ' ' + this.roundToThousandthPlace(xc1) + ',' + this.roundToThousandthPlace(y2) +
          ' ' + this.roundToThousandthPlace(x2) + ',' + this.roundToThousandthPlace(y2) +
          'L' + this.roundToThousandthPlace(x2) + ',' + this.roundToThousandthPlace(y2 + height2) +
          'C' + this.roundToThousandthPlace(xc1) + ',' + this.roundToThousandthPlace(y2 + height2) +
          ' ' + this.roundToThousandthPlace(xc0) + ',' + this.roundToThousandthPlace(y1 + height1) +
          ' ' + this.roundToThousandthPlace(x1) + ',' + this.roundToThousandthPlace(y1 + height1)
  }

  roundToThousandthPlace (value) {
    return +(Math.round(value + 'e+3') + 'e-3')
  }

  getStateTooltipDisplayObject ({ stateName, value, denominator }) {
    return {
      primaryValue: denominator
        ? `${formatService.formatNumberRoundedToInteger(value)} of ${formatService.formatNumberRoundedToInteger(denominator)} (${formatService.formatPercentShortFromFraction(value, denominator)})`
        : formatService.formatNumberRoundedToInteger(value),
      secondaryLabel: 'State',
      secondaryValue: stateName
    }
  }

  getFlowTooltipDisplayObject ({ fromStateName, toStateName, value, denominator }) {
    return {
      primaryValue: denominator
        ? `${formatService.formatNumberRoundedToInteger(value)} of ${formatService.formatNumberRoundedToInteger(denominator)} (${formatService.formatPercentShortFromFraction(value, denominator)})`
        : formatService.formatNumberRoundedToInteger(value),
      secondaryLabel: 'Flow',
      secondaryValue: `${fromStateName} → ${toStateName}`
    }
  }
}

export const sankeyService = new SankeyService()
